import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import { Card, Select, Button } from '../../components/ui';

interface Provider {
	id: string;
	name: string;
	specialty: string;
	appointments: number;
	revenue: number;
}

interface DateRange {
	label: string;
	value: string;
}

const dateRanges: DateRange[] = [
	{ label: 'Today', value: 'today' },
	{ label: 'This Week', value: 'week' },
	{ label: 'Month To Date', value: 'month' },
	{ label: 'Year to Date', value: 'year' },
	{ label: 'Customize', value: 'custom' },
];

const businesses = [
	{ label: 'All', value: 'all' },
	{ label: 'Trendit', value: 'trendit' },
	{ label: 'B4B', value: 'b4b' },
	{ label: '200Below', value: '200below' },
];

// Mock data
const topProviders: Provider[] = [
	{ id: 'keisha-b', name: 'Keisha B.', specialty: 'Tribal Braids Specialist', appointments: 18, revenue: 4320 },
	{ id: 'musu-k', name: 'Musu K.', specialty: 'Knotless Expert', appointments: 15, revenue: 3750 },
	{ id: 'rita-s', name: 'Rita S.', specialty: 'Box Braids Specialist', appointments: 12, revenue: 2880 },
	{ id: 'asha-n', name: 'Asha N.', specialty: 'Natural Styles', appointments: 9, revenue: 1530 },
];

const SuperAdminDashboard = () => {
	const navigate = useNavigate();
	const [selectedBusiness, setSelectedBusiness] = useState('all');
	const [selectedDateRange, setSelectedDateRange] = useState('week');
	const [customDates, setCustomDates] = useState({ from: '', to: '' });

	const showCustomDates = selectedDateRange === 'custom';

	const kpiData = useMemo(() => ({
		bookings: 34,
		revenue: 12480,
		deposits: 3240,
		customers: 127,
		providers: 8,
		commission: 4680,
	}), [selectedBusiness, selectedDateRange]);

	const topServices = [
		{ name: 'Knotless Medium', bookings: 14 },
		{ name: 'Tribal Braids', bookings: 9 },
		{ name: 'Box Braids', bookings: 7 },
	];

	return (
		<AppLayout
			topbarContent={
				<>
					<Select
						value={selectedBusiness}
						onChange={e => setSelectedBusiness(e.target.value)}
						className="w-full sm:w-auto sm:min-w-[160px] sm:max-w-[200px]"
					>
						{businesses.map(biz => (
							<option key={biz.value} value={biz.value}>
								{biz.label}
							</option>
						))}
					</Select>

					<Select
						value={selectedDateRange}
						onChange={e => setSelectedDateRange(e.target.value)}
						className="w-full sm:w-auto sm:min-w-[160px] sm:max-w-[200px]"
					>
						{dateRanges.map(range => (
							<option key={range.value} value={range.value}>
								{range.label}
							</option>
						))}
					</Select>

					{showCustomDates && (
						<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
							<input
								type="date"
								value={customDates.from}
								onChange={e => setCustomDates(prev => ({ ...prev, from: e.target.value }))}
								className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer w-full sm:w-auto sm:min-w-[140px]"
							/>
							<span className="text-gray-600 dark:text-gray-400 text-sm text-center sm:text-left">to</span>
							<input
								type="date"
								value={customDates.to}
								onChange={e => setCustomDates(prev => ({ ...prev, to: e.target.value }))}
								className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer w-full sm:w-auto sm:min-w-[140px]"
							/>
						</div>
					)}
				</>
			}
		>
			{/* KPI Cards Row 1 */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
				<Card className="bg-white dark:bg-gray-800">
					<div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Bookings (This Week)</div>
					<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{kpiData.bookings}</div>
				</Card>

				<Card className="bg-white dark:bg-gray-800">
					<div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Revenue</div>
					<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">${kpiData.revenue.toLocaleString()}</div>
				</Card>

				<Card className="bg-white dark:bg-gray-800">
					<div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Deposits</div>
					<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">${kpiData.deposits.toLocaleString()}</div>
				</Card>

				<Card className="bg-white dark:bg-gray-800">
					<div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Customers</div>
					<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{kpiData.customers}</div>
				</Card>
			</div>

			{/* KPI Cards Row 2 */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
				<Card className="bg-white dark:bg-gray-800">
					<div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Providers</div>
					<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{kpiData.providers}</div>
				</Card>

				<Card className="bg-white dark:bg-gray-800">
					<div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Commission</div>
					<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">${kpiData.commission.toLocaleString()}</div>
				</Card>
			</div>

			{/* Main Content Grid */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
				{/* Top Providers */}
				<div className="lg:col-span-2">
					<Card>
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 m-0">Top Providers (by Revenue)</h3>
							<Button size="sm" variant="primary" onClick={() => navigate('/providers')}>
								View All Providers
							</Button>
						</div>

						<div className="flex flex-col gap-3">
							{topProviders.map(provider => (
								<div
									key={provider.id}
									className="bg-white dark:bg-gray-700 p-4 rounded-lg flex justify-between items-center shadow-sm cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md"
									onClick={() => navigate(`/providers/${provider.id}`)}
								>
									<div>
										<div className="font-bold text-gray-900 dark:text-gray-100">{provider.name}</div>
										<div className="text-sm text-gray-600 dark:text-gray-400">
											{provider.appointments} appointments · {provider.specialty}
										</div>
									</div>
									<div className="text-xl font-bold text-blue-600 dark:text-blue-400">${provider.revenue.toLocaleString()}</div>
								</div>
							))}
						</div>
					</Card>
				</div>

				{/* Top Services */}
				<div>
					<Card>
						<h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 mt-0">Top Services</h3>
						<div className="space-y-3">
							{topServices.map((service, idx) => (
								<div key={idx} className="text-sm text-gray-700 dark:text-gray-300">
									<div className="font-semibold">{service.name}</div>
									<div className="text-gray-600 dark:text-gray-400">{service.bookings} bookings</div>
								</div>
							))}
						</div>
					</Card>
				</div>
			</div>

			{/* Footer */}
			<div className="mt-8 text-sm text-gray-500 dark:text-gray-400 text-center">© Beauty Business Portal</div>
		</AppLayout>
	);
};

export default SuperAdminDashboard;
